import { lazy, Suspense } from "react";
import { Route, Routes } from "react-router-dom";

import { AppLayout } from "@/components/layout/AppLayout";
import { CustomerLayout } from "@/components/layout/CustomerLayout";
import {
  RequireAuth,
  RequireCustomer,
  RequireRole,
  RequireStaff,
} from "@/components/layout/guards";
import { Spinner } from "@/components/ui/card";
import { ADMIN_ROLES } from "@/lib/auth";
import { ClaimsPage } from "@/pages/ClaimsPage";
import { CatalogPage } from "@/pages/catalog/CatalogPage";
import { ProductFormPage } from "@/pages/catalog/ProductFormPage";
import { ProductPage } from "@/pages/catalog/ProductPage";
import { TelegramBridge } from "@/components/TelegramBridge";
import { TelegramPage } from "@/pages/TelegramPage";
import { SetupPage } from "@/pages/setup/SetupPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { CompanyPage } from "@/pages/CompanyPage";
import { ForgotPasswordPage } from "@/pages/ForgotPasswordPage";
import { ProfilePage } from "@/pages/ProfilePage";
import { TenantsPage } from "@/pages/TenantsPage";
import { ServerPage } from "@/pages/server/ServerPage";
import { AnalyticsPage } from "@/pages/AnalyticsPage";
import { LeadsPage } from "@/pages/LeadsPage";
import { SalesPage } from "@/pages/SalesPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { LoginPage } from "@/pages/LoginPage";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { PropertiesPage } from "@/pages/PropertiesPage";
import { CustomersPage, ElectriciansPage } from "@/pages/UsersPage";
import { ClaimPage } from "@/pages/customer/ClaimPage";
import { MyHomesPage } from "@/pages/customer/MyHomesPage";
import { InfoTab, PropertyCreatePage } from "@/pages/property/InfoTab";
import { MediaTab } from "@/pages/property/MediaTab";
import { OwnerTab } from "@/pages/property/OwnerTab";
import { PanelsTab } from "@/pages/property/PanelsTab";
import { PropertyDetailPage } from "@/pages/property/PropertyDetailPage";
import { QrTab } from "@/pages/property/QrTab";
import { StructureTab } from "@/pages/property/StructureTab";

// Muharrirlar og'ir (SVG eksport, react-dom/server): faqat ochilganda yuklanadi
const AnnotationEditorPage = lazy(() =>
  import("@/pages/editors/AnnotationEditorPage").then((m) => ({ default: m.AnnotationEditorPage })),
);
const SchemeEditorPage = lazy(() =>
  import("@/pages/editors/SchemeEditorPage").then((m) => ({ default: m.SchemeEditorPage })),
);

const Lazy = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<Spinner className="m-10 size-8" />}>{children}</Suspense>
);

export function App() {
  return (
    <>
      <TelegramBridge />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot" element={<ForgotPasswordPage />} />
        <Route path="/setup" element={<SetupPage />} />
        <Route path="/tg" element={<TelegramPage />} />
        <Route element={<RequireAuth />}>
          <Route element={<RequireCustomer />}>
            <Route element={<CustomerLayout />}>
              <Route path="my" element={<MyHomesPage />} />
              <Route path="my/claim" element={<ClaimPage />} />
              <Route path="my/profile" element={<ProfilePage />} />
              <Route path="my/catalog" element={<CatalogPage mode="customer" />} />
              <Route path="my/catalog/:id" element={<ProductPage mode="customer" />} />
            </Route>
          </Route>
          <Route element={<RequireStaff />}>
            <Route element={<AppLayout />}>
              <Route index element={<DashboardPage />} />
              <Route path="properties" element={<PropertiesPage />} />
              <Route path="properties/new" element={<PropertyCreatePage />} />
              <Route
                path="properties/:id/annotate/:mediaId"
                element={
                  <Lazy>
                    <AnnotationEditorPage />
                  </Lazy>
                }
              />
              <Route
                path="properties/:id/scheme/:wallId"
                element={
                  <Lazy>
                    <SchemeEditorPage />
                  </Lazy>
                }
              />
              <Route path="properties/:id" element={<PropertyDetailPage />}>
                <Route index element={<InfoTab />} />
                <Route path="structure" element={<StructureTab />} />
                <Route path="panels" element={<PanelsTab />} />
                <Route path="media" element={<MediaTab />} />
                <Route path="qr" element={<QrTab />} />
                <Route path="owner" element={<OwnerTab />} />
              </Route>
              <Route
                path="customers"
                element={
                  <RequireRole roles={ADMIN_ROLES}>
                    <CustomersPage />
                  </RequireRole>
                }
              />
              <Route
                path="electricians"
                element={
                  <RequireRole roles={ADMIN_ROLES}>
                    <ElectriciansPage />
                  </RequireRole>
                }
              />
              <Route path="catalog" element={<CatalogPage mode="staff" />} />
              <Route path="catalog/new" element={<ProductFormPage />} />
              <Route path="catalog/:id" element={<ProductPage mode="staff" />} />
              <Route path="catalog/:id/edit" element={<ProductFormPage />} />
              <Route path="analytics" element={<AnalyticsPage />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route
                path="company"
                element={
                  <RequireRole roles={ADMIN_ROLES}>
                    <CompanyPage />
                  </RequireRole>
                }
              />
              <Route
                path="tenants"
                element={
                  <RequireRole roles={["super_admin"]}>
                    <TenantsPage />
                  </RequireRole>
                }
              />
              <Route
                path="settings"
                element={
                  <RequireRole roles={["super_admin"]}>
                    <SettingsPage />
                  </RequireRole>
                }
              />
              <Route
                path="server"
                element={
                  <RequireRole roles={["super_admin"]}>
                    <ServerPage />
                  </RequireRole>
                }
              />
              <Route path="leads" element={<LeadsPage />} />
              <Route path="sales" element={<SalesPage />} />
              <Route
                path="claims"
                element={
                  <RequireRole roles={ADMIN_ROLES}>
                    <ClaimsPage />
                  </RequireRole>
                }
              />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Route>
        </Route>
      </Routes>
    </>
  );
}
